import { useContext, useState } from 'react';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useQueryClient } from '@tanstack/react-query';
import { AdminMatches } from '@platform/client';
import { AdminTableContext } from '../../AdminTable';
import {
  FormState,
  SubmitHandler,
  useForm,
  UseFormRegister,
  Controller,
  Control,
  UseFormWatch,
} from 'react-hook-form';
import { InlineFormInput, DatePicker, FormsCheckBox } from '@platform/components';

type NewTeamMatchFormData = {
  teamId: number;
  projectId: number;
  sendEmail: boolean;
  scheduledSendDate?: Date;
};

export const CreateTeamMatchModal = (props: { onClose: () => void }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { formState, register, handleSubmit, control, watch } = useForm<NewTeamMatchFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const queryClient = useQueryClient();
  const newMatchMutator = AdminMatches.useAdminCreateTeamMatch({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminMatches.getGetAdminMatchesQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewTeamMatchFormData> = (data) => {
    newMatchMutator.mutate({
      data: {
        teamId: data.teamId,
        projectId: data.projectId,
        sendEmail: data.sendEmail ?? false,
        scheduledSendDate: data.scheduledSendDate?.toISOString(),
      },
    });
  };

  return (
    <AdminFormModal
      title='Create Team Match'
      onClose={props.onClose}
      modalStage={modalStage}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <NewTeamMatchForm formState={formState} register={register} control={control} watch={watch} />
    </AdminFormModal>
  );
};

const NewTeamMatchForm = (props: {
  formState: FormState<NewTeamMatchFormData>;
  register: UseFormRegister<NewTeamMatchFormData>;
  control: Control<NewTeamMatchFormData>;
  watch: UseFormWatch<NewTeamMatchFormData>;
}) => {
  const { formState, register, control, watch } = props;
  return (
    <div className='space-y-2'>
      <InlineFormInput
        register={() => register('teamId', { required: 'Team ID is required' })}
        label='Team ID'
        placeholder='Enter team id'
        errors={formState.errors.teamId}
      />
      <InlineFormInput
        register={() => register('projectId', { required: 'Project ID is required' })}
        label='Project ID'
        placeholder='Enter a project id'
        errors={formState.errors.projectId}
      />
      <FormsCheckBox
        size='sm'
        description='Send propose match email to rep and schedule reminders'
        control={control}
        name='sendEmail'
      />
      {watch('sendEmail') && (
        <Controller
          control={control}
          name='scheduledSendDate'
          render={({ field }) => (
            <DatePicker
              label='Schedule email (leave blank to send immediately)'
              initialDate={field.value ?? undefined}
              onChange={field.onChange}
            />
          )}
        />
      )}
    </div>
  );
};
